import jsPDF from 'jspdf';
import QRCode from 'qrcode';
import { Temporal } from 'temporal-polyfill';
import { StudentYear, studentYearOption } from '@airreading/common/data';
import { groupStudentsByGrade, StudentWithQrCode } from './student-utils';

/**
 * Generates a QR code data URL for the given URL.
 */
async function generateQrCodeDataUrl(url: string): Promise<string> {
  return QRCode.toDataURL(url, {
    width: 200,
    margin: 1,
    color: {
      dark: '#000000',
      light: '#FFFFFF',
    },
  });
}

interface StudentPageData {
  studentName: string;
  shortCode: string;
  grade: StudentYear | 'no_grade';
  qrCodeDataUrl: string;
}

/**
 * Adds a student page to the PDF with QR code, name, and grade information.
 */
async function addStudentPageToPdf(
  pdf: jsPDF,
  { studentName, shortCode, grade, qrCodeDataUrl }: StudentPageData,
  pageWidth: number,
  margin: number,
): Promise<void> {
  // Grade label (blue, top-left)
  const gradeLabel = grade === 'no_grade' ? 'No Grade' : `${studentYearOption(grade)} Grade`;
  pdf.setTextColor(0, 0, 255); // Blue color
  pdf.setFontSize(14);
  pdf.setFont('helvetica', 'bold');
  pdf.text(gradeLabel, margin, margin + 10);

  // Student name (centered as title)
  pdf.setTextColor(0, 0, 0); // Black color
  pdf.setFontSize(16);
  pdf.setFont('helvetica', 'bold');
  const nameY = margin + 30;
  const nameWidth = pdf.getTextWidth(studentName);
  const nameX = (pageWidth - nameWidth) / 2; // Center horizontally
  pdf.text(studentName, nameX, nameY);

  // Short link (stylized as "Go.AirReading.com/Shortcode")
  pdf.setFontSize(12);
  pdf.setFont('helvetica', 'normal');
  const shortLinkText = `Go.AirReading.com/${shortCode}`;
  const shortLinkY = nameY + 10;
  const shortLinkWidth = pdf.getTextWidth(shortLinkText);
  const shortLinkX = (pageWidth - shortLinkWidth) / 2; // Center horizontally
  pdf.text(shortLinkText, shortLinkX, shortLinkY);

  // QR code (centered, below the text)
  const qrCodeSize = 60;
  const qrCodeX = (pageWidth - qrCodeSize) / 2;
  const qrCodeY = shortLinkY + 20;

  // Convert data URL to image and add to PDF
  const img = new Image();
  img.src = qrCodeDataUrl;
  await new Promise<void>((resolve, reject) => {
    const timeout = setTimeout(() => {
      reject(new Error('QR code image loading timeout'));
    }, 10000); // 10 second timeout

    img.onload = () => {
      clearTimeout(timeout);
      try {
        pdf.addImage(qrCodeDataUrl, 'PNG', qrCodeX, qrCodeY, qrCodeSize, qrCodeSize);
        resolve();
      } catch (error) {
        reject(new Error(`Failed to add QR code to PDF: ${error instanceof Error ? error.message : 'Unknown error'}`));
      }
    };
    img.onerror = () => {
      clearTimeout(timeout);
      reject(new Error('Failed to load QR code image'));
    };
  });
}

/**
 * Processes a single student: generates QR code and adds page to PDF.
 */
async function processStudent(
  pdf: jsPDF,
  student: StudentWithQrCode,
  grade: StudentYear | 'no_grade',
  pageWidth: number,
  margin: number,
  isFirstPage: boolean,
): Promise<void> {
  const shortCode = student.shortCode;
  if (!shortCode) {
    return; // Skip students without shortCode (defensive check)
  }

  if (!isFirstPage) {
    pdf.addPage();
  }

  const fullLinkUrl = `${import.meta.env.VITE_JOIN_NOW_URL}/${shortCode}`;
  const studentName =
    student.fullNameOrEmail || `${student.firstName || ''} ${student.lastName || ''}`.trim() || 'Unknown';

  const qrCodeDataUrl = await generateQrCodeDataUrl(fullLinkUrl);

  await addStudentPageToPdf(
    pdf,
    {
      studentName,
      shortCode,
      grade,
      qrCodeDataUrl,
    },
    pageWidth,
    margin,
  );
}

/**
 * Generates a PDF with QR codes for students, one page per student.
 * Students are grouped by grade level and sorted alphabetically by name within each grade.
 */
export async function generateQrCodesPdf(students: StudentWithQrCode[]): Promise<void> {
  const { studentsByGrade, orderedGrades } = groupStudentsByGrade(students);

  const pdf = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4',
  });

  const pageWidth = pdf.internal.pageSize.getWidth();
  const margin = 20;

  const orderedStudents = orderedGrades.flatMap((grade) => {
    const gradeStudents = studentsByGrade.get(grade) || [];
    return gradeStudents.map((student) => ({ student, grade }));
  });

  // Process students sequentially (required for PDF page order)
  for (let i = 0; i < orderedStudents.length; i++) {
    const { student, grade } = orderedStudents[i];
    await processStudent(pdf, student, grade, pageWidth, margin, i === 0);
  }

  const date = Temporal.Now.plainDateISO().toString();
  pdf.save(`student_qr_codes_${date}.pdf`);
}
